/**
 * Sortable — thin wrappers around @dnd-kit/sortable that handle the
 * 90% case of reordering a list of items with a visible drag handle.
 *
 * Usage:
 *
 *   <SortableList ids={items.map(i => i.id)}
 *                 onReorder={(oldIdx, newIdx) => ...}>
 *     {items.map(item => (
 *       <SortableItem key={item.id} id={item.id}>
 *         {({ handleProps, isDragging }) => (
 *           <div>
 *             <button {...handleProps}><GripVertical /></button>
 *             ...row contents...
 *           </div>
 *         )}
 *       </SortableItem>
 *     ))}
 *   </SortableList>
 *
 * The handle props (`handleProps`) MUST be spread on an interactive
 * element (button/div) inside the row — that's what initiates the
 * drag. Spreading them on the whole row would conflict with text
 * inputs and other interactive widgets inside the row.
 */
import React from "react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


/**
 * Compute next array given the old + new indices from a drag event.
 * Exposed as a helper so callers can build the next state shape and
 * trigger an autosave with the new array in one go (no stale
 * setState closures).
 */
export function applyReorder(list, fromIdx, toIdx) {
  if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return list;
  return arrayMove(list, fromIdx, toIdx);
}


export function SortableList({ ids, onReorder, children }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a 5px drag before the gesture is treated as a sort —
      // prevents accidental drags when clicking a handle.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(oldIdx, newIdx);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}


export function SortableItem({ id, disabled = false, children }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // Lift dragging row above siblings so dropshadow shows cleanly.
    zIndex: isDragging ? 10 : "auto",
  };
  const handleProps = { ...attributes, ...listeners };
  return (
    <div ref={setNodeRef} style={style}>
      {typeof children === "function"
        ? children({ handleProps, isDragging })
        : children}
    </div>
  );
}
